// Trace estilo Quake: swept AABB contra brushes convexos (listas de planos).
// A caixa é reduzida a um ponto expandindo cada plano pelo suporte da AABB (Minkowski).
// Referência conceitual: CM_ClipBoxToBrush (Quake/Source). docs/02-MOVEMENT.md.
import type { Plane } from '@surfrun/shared';
import { V3, v3, copy, dot, addScaled } from './vec';

const DIST_EPSILON = 0.03125; // 1/32, igual ao Source

export interface Brush {
  planes: Plane[];
  surf: boolean;
  mat: string;
  // AABB do brush (broadphase) — calculada no carregamento do mapa
  min: V3;
  max: V3;
}

export interface TraceResult {
  fraction: number;     // 0..1 do movimento completado
  endPos: V3;
  normal: V3;           // normal do plano de impacto (válida se hit)
  hit: boolean;
  startSolid: boolean;  // começou dentro de sólido
  allSolid: boolean;    // início E fim dentro de sólido
}

export function makeTrace(): TraceResult {
  return { fraction: 1, endPos: v3(), normal: v3(0, 1, 0), hit: false, startSolid: false, allSolid: false };
}

/**
 * Clipa o segmento start→end (centro da AABB com meia-extensão half) contra um brush.
 * Atualiza tr se este brush produzir colisão mais próxima.
 */
export function clipBoxToBrush(
  tr: TraceResult, brush: Brush, start: V3, end: V3, half: V3,
): void {
  let enterFrac = -1;
  let leaveFrac = 1;
  let clipPlane: Plane | null = null;
  let getout = false;
  let startout = false;

  for (const pl of brush.planes) {
    const n = pl.n;
    // expande o plano pelo suporte da caixa (normal aponta pra fora do brush)
    const dist = pl.d + Math.abs(n[0]) * half.x + Math.abs(n[1]) * half.y + Math.abs(n[2]) * half.z;
    const d1 = n[0] * start.x + n[1] * start.y + n[2] * start.z - dist;
    const d2 = n[0] * end.x + n[1] * end.y + n[2] * end.z - dist;

    if (d2 > 0) getout = true;   // termina fora deste plano
    if (d1 > 0) startout = true; // começa fora deste plano

    // completamente na frente do plano e não entrando → sem colisão com o brush
    if (d1 > 0 && (d2 >= DIST_EPSILON || d2 >= d1)) return;
    // completamente atrás do plano → não restringe
    if (d1 <= 0 && d2 <= 0) continue;

    if (d1 > d2) {
      // entrando no brush por este plano
      const f = (d1 - DIST_EPSILON) / (d1 - d2);
      if (f > enterFrac) { enterFrac = f; clipPlane = pl; }
    } else {
      // saindo do brush
      const f = (d1 + DIST_EPSILON) / (d1 - d2);
      if (f < leaveFrac) leaveFrac = f;
    }
  }

  if (!startout) {
    // começa dentro do brush
    tr.startSolid = true;
    if (!getout) tr.allSolid = true;
    return;
  }

  if (enterFrac < leaveFrac && enterFrac > -1 && enterFrac < tr.fraction && clipPlane) {
    tr.fraction = Math.max(0, enterFrac);
    tr.normal.x = clipPlane.n[0];
    tr.normal.y = clipPlane.n[1];
    tr.normal.z = clipPlane.n[2];
    tr.hit = true;
  }
}

/** Trace completo contra uma lista de brushes (broadphase por AABB do sweep). */
export function traceBox(
  brushes: readonly Brush[], start: V3, end: V3, half: V3, out?: TraceResult,
): TraceResult {
  const tr = out ?? makeTrace();
  tr.fraction = 1; tr.hit = false; tr.startSolid = false; tr.allSolid = false;

  const minX = Math.min(start.x, end.x) - half.x, maxX = Math.max(start.x, end.x) + half.x;
  const minY = Math.min(start.y, end.y) - half.y, maxY = Math.max(start.y, end.y) + half.y;
  const minZ = Math.min(start.z, end.z) - half.z, maxZ = Math.max(start.z, end.z) + half.z;

  for (const b of brushes) {
    if (b.min.x > maxX || b.max.x < minX || b.min.y > maxY || b.max.y < minY || b.min.z > maxZ || b.max.z < minZ) continue;
    clipBoxToBrush(tr, b, start, end, half);
    if (tr.allSolid) break;
  }

  copy(tr.endPos, start);
  if (tr.fraction > 0) addScaled(tr.endPos, start, tmpDelta(start, end), tr.fraction);
  return tr;
}

const _delta = v3();
function tmpDelta(start: V3, end: V3): V3 {
  _delta.x = end.x - start.x; _delta.y = end.y - start.y; _delta.z = end.z - start.z;
  return _delta;
}

/** ClipVelocity do Quake: remove o componente da velocidade que entra no plano. */
export function clipVelocity(vel: V3, normal: V3, overbounce: number): void {
  const backoff = dot(vel, normal) * overbounce;
  vel.x -= normal.x * backoff;
  vel.y -= normal.y * backoff;
  vel.z -= normal.z * backoff;
  // corrige resíduo de ponto flutuante ainda penetrando
  const adjust = dot(vel, normal);
  if (adjust < 0) {
    vel.x -= normal.x * adjust;
    vel.y -= normal.y * adjust;
    vel.z -= normal.z * adjust;
  }
}
